import {
  Box,
  Button,
  Fab,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, DragEvent, useState } from "react";
import { DocumentIcon } from "./DocumentIcon";
import { UserDocument } from "../../models/UserDocument";
import { atticusColors } from "theme";
import { Upload } from "@mui/icons-material";

const AcceptedFileFormats = () => {
  return (
    <Typography
      variant="body2"
      sx={{
        py: 2,
      }}
    >
      50mb size limit
    </Typography>
  );
};

interface RenderProps {
  uploadedFiles?: FileList;
  previewImage?: JSX.Element;
  shouldPreview?: boolean,
}
export function RenderFileUploadArea(props: RenderProps): JSX.Element {
  const uploadedFiles = props.uploadedFiles;
  if (props.previewImage && (!uploadedFiles || !uploadedFiles.item || uploadedFiles.length === 0)) {
    return props.previewImage;
  }

  const uploadComponent = (
    <Box key={"previewImageBox1"}>
      <Fab size="large" color="primary" sx={{ boxShadow: 0, marginBottom: "1rem" }} >
        <Upload fontSize="large" />
      </Fab>
      <Typography
        variant="h4"
        color="primary"
        sx={{
          pb: 2,
          fontWeight: 600,
        }}
      >
        Drag or upload a file here
      </Typography>
      <AcceptedFileFormats />
      <Button
        variant="contained"
        color="primary"
      >
        Choose a file
      </Button>
    </Box>
  );
  if (!uploadedFiles || !uploadedFiles.item || uploadedFiles.length === 0) {
    return uploadComponent;
  }
  const file: File | null = uploadedFiles.item(0);
  if (!file) {
    return uploadComponent;
  }

  // We have a file
  if (file.type.indexOf("image/") === 0 && file.type.indexOf("svg") === -1) {
    const objectUrl = URL.createObjectURL(file.slice());
    return (
      <Box key={"previewImageBox2"}>
        <img
          src={objectUrl}
          style={{ maxWidth: "220px", maxHeight: "220px", minHeight: "120px", minWidth: "120px" }}
        />
        <Typography variant="h3" style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", marginTop: "1rem" }}>
          {file.name}
        </Typography>
      </Box>
    );
  } else if (file.type.indexOf("application/pdf") === 0) {
    const objectUrl = URL.createObjectURL(file) + "#toolbar=0";
    return (
      <Box key={"previewImageBox3"}>
        <object
          data={objectUrl}
          type="application/pdf"
          style={{
            border: "0",
            minWidth: "300px",
            minHeight: "365px",
            width: "100%",
            pointerEvents: "none",
          }}
        >
          <iframe
            src={objectUrl}
            style={{
              border: "0",
              minWidth: "300px",
              minHeight: "365px",
              width: "100%",
              pointerEvents: "none",
            }}
          >
            This browser does not support PDFs. Please download the PDF to view it
          </iframe>
        </object>
        <Typography variant="h3" style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", marginTop: "1rem" }}>
          {file.name}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxHeight: "400px",
      }}
      key={"previewImageBox4"}
    >
      <DocumentIcon extension={file?.type} />
      <Typography variant="h3" style={{ marginTop: "1rem" }}>{file.name}</Typography>
    </Box>
  );
}

interface RenderFileUploadBoxInterface {
  uploadedFiles?: FileList;
  setErrors: (errs: string[]) => void;
  setUploadedFiles: (inputFiles: FileList) => void;
  onPreviewClick?: () => void,
  previewImage?: JSX.Element;
  document?: UserDocument,
  documentSrc?: string,
}

export function RenderFileUploadBox(props: RenderFileUploadBoxInterface): JSX.Element {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isNew = !props.document?.id
  const shouldPreview = (!isNew && (props.documentSrc || props.document?.version?.cloudUrl))

  // triggers the input when the button is clicked
  function handleChooseClick() {
    inputRef?.current?.click();
  }

  // handle drag events
  const handleDrag = function (e: DragEvent<HTMLFormElement | HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const handleDrop = function (e: DragEvent<HTMLDivElement>) {
    props.setErrors([]);
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // at least one file has been dropped so do something
      validateFile(e.dataTransfer.files);
    }
  };

  // triggers when file is selected with click
  const handleChange = function (e: ChangeEvent<HTMLInputElement>) {
    props.setErrors([]);
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      // at least one file has been selected so do something
      validateFile(e.target.files);
    }
  };

  const validateFile = async (inputFiles: FileList): Promise<void> => {
    if (inputFiles.length === 0) {
      props.setErrors(["Please drop or select a file."]);
      return;
    }

    // allow multiple?
    const errs: string[] = [];
    for (let i = 0; i < inputFiles.length; i++) {
      const file: File | null = inputFiles.item(i);
      if (!file) {
        errs.push("no file");
      }
    }
    props.setUploadedFiles(inputFiles);
    if (errs?.length) {
      props.setErrors(errs);
    }
  };

  return (
    <>
      <Box
        sx={{
          marginTop: "1rem",
          marginBottom: "0.5rem",
          borderWidth: 2,
          borderRadius: 2,
          borderStyle: "dashed",
          borderColor: atticusColors.fileBorderBlue,
          backgroundColor: isNew && dragActive ? "white" : atticusColors.fileBackgroundBlue,
        }}
        onDragEnter={!shouldPreview ? (e) => handleDrag(e) : () => { }}
        style={{ width: "100%", height: "100%" }}
        onClick={(e) => {
          e.stopPropagation()

          if (shouldPreview) {
            props.onPreviewClick?.()
          } else {
            handleChooseClick()
          }
        }}
      >
        <Box
          key={"boxthatcontainsfileinput"}
          style={{
            textAlign: "center",
            position: "relative",
            minHeight: "220px",

          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              cursor: "pointer",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 16px",

            }}
          >
            {dragActive && (
              <Box
                key={"newfiledragboxactive"}
                id="drag-file-element"
                onDragEnter={!shouldPreview ? (e) => handleDrag(e) : () => { }}
                onDragLeave={!shouldPreview ? (e) => handleDrag(e) : () => { }}
                onDragOver={!shouldPreview ? (e) => handleDrag(e) : () => { }}
                onDrop={!shouldPreview ? (e) => handleDrop(e) : () => { }}
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                }}
              />
            )}
            <div>
              <RenderFileUploadArea
                uploadedFiles={props.uploadedFiles}
                previewImage={props.previewImage}
                shouldPreview={!!shouldPreview}
              />
            </div>
            {shouldPreview && (props.documentSrc || props.document?.version?.cloudUrl) && <div>
              <Button variant="contained" color="secondary" onClick={() => { }}>Open preview</Button>
            </div>}
          </div>
        </Box>
      </Box>
      <input
        type="file"
        ref={inputRef}
        id="input-file-upload"
        multiple={false}
        style={{ display: "none" }}
        onChange={handleChange}
      />
      {/* I can't get the button to work properly without just putting it in a label */}
      {!props.document?.version?.cloudUrl && (props.documentSrc || (props.uploadedFiles && props.uploadedFiles.length > 0)) &&
        <label htmlFor="input-file-upload" >
          <div style={{ cursor: "pointer" }}>
            <Button variant="text" color="primary" style={{ pointerEvents: "none", cursor: "pointer" }}>
              Upload or update file
            </Button>
          </div>
        </label >}
    </>
  );
}
